import { isSocialsBlock, isVideoBlock, SOCIAL_PLATFORMS } from "@simplyk/common";
import { TReaderBlock, TReaderDocument } from "@usewaypoint/email-builder";

import { AuditErrors } from "@/components/FullScreenEmailEditorContainer/types";

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Matches `href` values that are either empty or still set to a platform's
// default placeholder URL (e.g. `https://facebook.com/`, `mailto:`)
const PLACEHOLDER_SOCIAL_HREF_REGEX = new RegExp(
  `href="(?:${Object.values(SOCIAL_PLATFORMS)
    .map((platform) => platform.url)
    .filter((url) => url.length > 0)
    .map(escapeRegExp)
    .join("|")}|)"`,
  "g"
);

export const BLOCKED_LINK_SHORTENER_DOMAINS = [
  "bit.ly",
  "tinyurl.com",
  "ow.ly",
  "goo.gl",
  "t.co",
  "buff.ly",
  "dlvr.it",
  "ift.tt",
  "shorte.st",
  "adf.ly",
  "tiny.cc",
  "is.gd",
  "v.gd",
  "bc.vc",
  "rb.gy",
  "cutt.ly",
];

const BLOCKED_SHORTENER_URL_REGEX = new RegExp(
  `^(?:https?://)?(?:www\\.)?(?:${BLOCKED_LINK_SHORTENER_DOMAINS.map(escapeRegExp).join("|")})(?:[/?#]|$)`,
  "i"
);

const HREF_VALUE_REGEX = /href="([^"]+)"/g;
const VIDEO_WITHOUT_LINK_REGEX = /<a\b(?=[^>]*\bdata-video-link\b)(?![^>]*\bhref="[^"]+")[^>]*>/g;

const extractAllLinks = (blocks: TReaderBlock[]): string[] => {
  const urls: string[] = [];

  for (const block of blocks) {
    if (block.type === "Button") {
      const url = (block as Extract<TReaderBlock, { type: "Button" }>).data.props?.url;
      if (url) {
        urls.push(url);
      }
    }

    if (block.type === "Image") {
      const linkHref = (block as Extract<TReaderBlock, { type: "Image" }>).data.props?.linkHref;
      if (linkHref) {
        urls.push(linkHref);
      }
    }

    if (block.type === "Html") {
      const contents = (block as Extract<TReaderBlock, { type: "Html" }>).data.props?.contents;
      if (contents) {
        for (const match of contents.matchAll(HREF_VALUE_REGEX)) {
          urls.push(match[1]);
        }
      }
    }
  }

  return urls;
};

/**
 * Counts clickable links in a raw HTML string (e.g. a Froala WYSIWYG email body) whose host is a
 * public URL shortener blocked by SendGrid. Automated/transactional emails (thank-you, reminders,
 * renewals, fulfillment) are authored as raw HTML rather than as a Waypoint document, so they can't
 * reuse `getEmailBodyAuditErrors`. This shares the same domain list and matching rules so both the
 * bulk-email builder and the automated-email cards stay in sync.
 */
export const countBlockedShortenerLinksInHtml = (html: unknown): number => {
  if (typeof html !== "string" || !html) {
    return 0;
  }

  let count = 0;
  for (const match of html.matchAll(HREF_VALUE_REGEX)) {
    if (BLOCKED_SHORTENER_URL_REGEX.test(match[1])) {
      count++;
    }
  }
  return count;
};

/**
 * Scans the email body for common authoring mistakes and returns one count per
 * error category. Helps users catch errors before sending the email:
 * - images still using the placeholder URL
 * - buttons without a link
 * - video embeds with an empty link
 * - socials blocks with empty hrefs
 * - links that use a public URL shortener blocked by SendGrid
 */
export const getEmailBodyAuditErrors = (designConfig: TReaderDocument): AuditErrors => {
  const blocks = Object.values(designConfig);

  const placeholderImage = blocks
    .filter((block): block is Extract<TReaderBlock, { type: "Image" }> => block.type === "Image")
    .filter((block) => !block.data.props?.url).length;

  const buttonWithoutLink = blocks
    .filter((block): block is Extract<TReaderBlock, { type: "Button" }> => block.type === "Button")
    .filter((block) => !block.data.props?.url).length;

  const htmlBlocks = blocks.filter((block): block is Extract<TReaderBlock, { type: "Html" }> => block.type === "Html");

  let videoWithoutLink = 0;
  let socialsWithoutLink = 0;
  for (const block of htmlBlocks) {
    const contents = block.data.props?.contents;
    if (!contents) {
      continue;
    }

    if (isVideoBlock(contents)) {
      videoWithoutLink += contents.match(VIDEO_WITHOUT_LINK_REGEX)?.length ?? 0;
    }
    if (isSocialsBlock(contents)) {
      socialsWithoutLink += contents.match(PLACEHOLDER_SOCIAL_HREF_REGEX)?.length ?? 0;
    }
  }

  const blockedLinkShortener = extractAllLinks(blocks).filter((url) => BLOCKED_SHORTENER_URL_REGEX.test(url)).length;

  return { placeholderImage, buttonWithoutLink, videoWithoutLink, socialsWithoutLink, blockedLinkShortener };
};

export const hasAuditErrors = (errors: AuditErrors): boolean =>
  errors.placeholderImage > 0 ||
  errors.buttonWithoutLink > 0 ||
  errors.videoWithoutLink > 0 ||
  errors.socialsWithoutLink > 0 ||
  errors.blockedLinkShortener > 0;
